import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'
import { ChevronRight } from 'lucide-react'
import { createContext, use } from 'react'

import { cn } from '../../../lib/utils.ts'

import { itemGroupVariants, itemTitleVariants, itemVariants } from './item.variants.ts'

type ItemSize = NonNullable<VariantProps<typeof itemVariants>['size']>

const ItemGroupContext = createContext<ItemSize | undefined>(undefined)

/**
 * Item is a composable list row: media + content (title/description) + actions.
 * Defaults to a `<div>`; pass `render={<Link .../>}` to make the whole row a link, and `interactive`
 * to give it the clickable affordance (cursor, hover fill, focus outline).
 * Group rows with `Item.Group`: `default` for spaced rows with no separator, `outline` for the bordered
 * card look, `divided` for separators only (rows lose their horizontal inset so they align with the
 * surrounding content).
 * `Item.Title` carries its own `overflow` (truncate / wrap) and `weight` axes.
 *
 * **Styling hooks**:
 * - `data-variant` on `Item.Media`: `icon` draws a 32px `rounded-20` box with a brand-gradient
 *   background and a 16px glyph; `image` and `default` draw nothing.
 * - `data-slot="item"` / `data-slot="item-group"` / `data-slot="item-listitem"`: the targeting hooks callers and
 *   `Item.Group`'s `divided` variant rely on (`[&>[data-slot=item-listitem]>[data-slot=item]]:px-0`).
 *
 * @summary List row compound (media / content / title / description / actions) + group + separator.
 */
function ItemRoot(props: ItemRoot.Props) {
  const { className, variant = 'default', size, interactive = false, render, ref, ...rest } = props
  const groupSize = use(ItemGroupContext)
  const resolvedSize = size ?? groupSize ?? 'medium'
  const element = useRender({
    render,
    ref,
    defaultTagName: 'div',
    props: {
      'data-slot': 'item',
      className: cn(itemVariants({ variant, size: resolvedSize, interactive, className })),
      ...rest,
    },
  })
  if (groupSize !== undefined) {
    return (
      <div role="listitem" data-slot="item-listitem">
        {element}
      </div>
    )
  }
  return element
}
namespace ItemRoot {
  type Variants = NonNullable<VariantProps<typeof itemVariants>>
  export type Props = useRender.ComponentProps<'div'> & {
    /** Surface treatment. @default 'default' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Vertical density. Inherits from Item.Group. @default 'medium' */
    size?: NonNullable<Variants['size']> | undefined
    /**
     * Clickable row: pointer cursor, hover fill and keyboard focus outline. Independent from `render`,
     * which only swaps the rendered element. @default false
     */
    interactive?: boolean
  }
}

function ItemGroup(props: ItemGroup.Props) {
  const { className, variant = 'default', size, children, ...rest } = props
  return (
    <ItemGroupContext.Provider value={size ?? 'medium'}>
      <div role="list" data-slot="item-group" className={cn(itemGroupVariants({ variant, className }))} {...rest}>
        {children}
      </div>
    </ItemGroupContext.Provider>
  )
}
namespace ItemGroup {
  type Variants = NonNullable<VariantProps<typeof itemGroupVariants>>
  export type Props = React.ComponentProps<'div'> & {
    variant?: NonNullable<Variants['variant']> | undefined
    /** Inherited by Item.Root children; an explicit Root size wins. @default 'medium' */
    size?: ItemSize | undefined
  }
}

function ItemMedia(props: ItemMedia.Props) {
  const { className, variant = 'default', ...rest } = props
  return (
    <div
      data-slot="item-media"
      data-variant={variant}
      className={cn(
        // TODO: Revisit this foreground when co-branding theming is consolidated.
        // Its token mapping currently depends on partner-specific overrides.
        `
          flex shrink-0 items-center justify-center text-icon-base
          data-[variant=icon]:size-[32px] data-[variant=icon]:rounded-20 data-[variant=icon]:bg-gradient-brand-light
          data-[variant=icon]:text-icon-item-brand-light
          [&_svg:not([class*='size-'])]:size-6
          data-[variant=icon]:[&_svg:not([class*='size-'])]:size-[16px]
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ItemMedia {
  export type Props = React.ComponentProps<'div'> & { variant?: 'icon' | 'image' | 'default' }
}

function ItemContent(props: ItemContent.Props) {
  const { className, ...rest } = props
  return <div data-slot="item-content" className={cn('flex min-w-0 flex-1 flex-col gap-20', className)} {...rest} />
}
namespace ItemContent {
  export type Props = React.ComponentProps<'div'>
}

function ItemTitle(props: ItemTitle.Props) {
  const { className, overflow = 'truncate', weight = 'regular', ...rest } = props
  return <div data-slot="item-title" className={cn(itemTitleVariants({ overflow, weight, className }))} {...rest} />
}
namespace ItemTitle {
  type Variants = NonNullable<VariantProps<typeof itemTitleVariants>>
  export type Props = React.ComponentProps<'div'> & {
    /** Single-line with an ellipsis, or wrapping onto several lines. @default 'truncate' */
    overflow?: NonNullable<Variants['overflow']> | undefined
    /** Font weight. @default 'semibold' */
    weight?: NonNullable<Variants['weight']> | undefined
  }
}

function ItemDescription(props: ItemDescription.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="item-description"
      className={cn('truncate text-20 leading-30 font-400 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace ItemDescription {
  export type Props = React.ComponentProps<'div'>
}

function ItemActions(props: ItemActions.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="item-actions"
      className={cn('ml-auto flex shrink-0 items-center gap-inline text-icon-base', className)}
      {...rest}
    />
  )
}
namespace ItemActions {
  export type Props = React.ComponentProps<'div'>
}

function ItemChevron(props: ItemChevron.Props) {
  const { className } = props
  return <ChevronRight aria-hidden="true" data-slot="item-chevron" size={16} className={cn(className)} />
}

namespace ItemChevron {
  export type Props = Pick<React.ComponentProps<typeof ChevronRight>, 'className'>
}

function ItemSeparator(props: ItemSeparator.Props) {
  const { className, ...rest } = props
  return (
    <div role="separator" data-slot="item-separator" className={cn('h-px bg-line-base-basic', className)} {...rest} />
  )
}
namespace ItemSeparator {
  export type Props = React.ComponentProps<'div'>
}

const Item = {
  Root: ItemRoot,
  Group: ItemGroup,
  Media: ItemMedia,
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Actions: ItemActions,
  Chevron: ItemChevron,
  Separator: ItemSeparator,
}

namespace Item {
  export namespace Root {
    export type Props = ItemRoot.Props
  }
  export namespace Group {
    export type Props = ItemGroup.Props
  }
  export namespace Media {
    export type Props = ItemMedia.Props
  }
  export namespace Content {
    export type Props = ItemContent.Props
  }
  export namespace Title {
    export type Props = ItemTitle.Props
  }
  export namespace Description {
    export type Props = ItemDescription.Props
  }
  export namespace Actions {
    export type Props = ItemActions.Props
  }
  export namespace Chevron {
    export type Props = ItemChevron.Props
  }
  export namespace Separator {
    export type Props = ItemSeparator.Props
  }
}

export { Item }
export { itemGroupVariants, itemTitleVariants, itemVariants } from './item.variants.ts'
